import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import RuleBlockDisplay from "components/rules/rule-builder/RuleBlockDisplay";
import RuleBlockForm from "components/rules/rule-builder/RuleBlockForm";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { Modal } from "components/bootstrap";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import { getDictForFunction } from "./helpers";
const BlockContainer = styled.div(
  ({ theme }) => css`
    padding-top: ${theme.spacings.xxs};
  `
);
const appSection = "pipeline-rule-builder";
const RuleBuilderBlock = ({
  type,
  blockDict,
  block = void 0,
  order,
  outputVariableList = [],
  addBlock,
  updateBlock,
  deleteBlock
}) => {
  const [currentBlockDict, setCurrentBlockDict] = useState(void 0);
  const [editMode, setEditMode] = useState(false);
  const [insertMode, setInsertMode] = useState(void 0);
  const [insertBlockDict, setInsertBlockDict] = useState(void 0);
  const sendTelemetry = useSendTelemetry();
  useEffect(() => {
    if (block) {
      setCurrentBlockDict(getDictForFunction(blockDict, block.function));
    }
  }, [block, blockDict]);
  const buildBlockData = (newData) => {
    const defaultParameters = { newFunctionName: currentBlockDict.name, newParams: {}, toggleNegate: false };
    const { newFunctionName, newParams, toggleNegate } = { ...defaultParameters, ...newData };
    const defaultBlock = { function: newFunctionName, params: {} };
    let newBlock;
    if (block && newFunctionName === block.function) {
      newBlock = block;
    } else {
      newBlock = defaultBlock;
    }
    if (toggleNegate) {
      newBlock.negate = !newBlock.negate;
    }
    return { ...newBlock, params: { ...newBlock.params, ...newParams } };
  };
  const resetBlock = () => {
    setEditMode(false);
    if (block) {
      setCurrentBlockDict(blockDict.find((b) => b.name === block.function));
    } else {
      setCurrentBlockDict(void 0);
    }
  };
  const resetInsertBlock = () => {
    setInsertMode(void 0);
    setInsertBlockDict(void 0);
  };
  const onCancel = () => {
    resetBlock();
    resetInsertBlock();
  };
  const onAdd = (paramsToAdd) => {
    addBlock(type, buildBlockData({ newParams: paramsToAdd }));
    onCancel();
  };
  const onDelete = () => {
    sendTelemetry(
      type === "condition" ? TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.DELETE_CONDITION_CLICKED : TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.DELETE_ACTION_CLICKED,
      {
        app_section: appSection,
        app_action_value: `delete-${type}-button`
      }
    );
    deleteBlock(order, type);
  };
  const onEdit = () => {
    sendTelemetry(
      type === "condition" ? TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.EDIT_CONDITION_CLICKED : TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.EDIT_ACTION_CLICKED,
      {
        app_section: appSection,
        app_action_value: `edit-${type}-button`
      }
    );
    setEditMode(true);
  };
  const onNegate = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.NEGATE_CONDITION_CLICKED, {
      app_section: appSection,
      app_action_value: `negate-${type}-button`
    });
    updateBlock(order, type, buildBlockData({ toggleNegate: true }));
  };
  const onDuplicate = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.DUPLICATE_ACTION_CLICKED, {
      app_section: appSection,
      app_action_value: `duplicate-${type}`
    });
    const duplicatedBlock = { ...block, outputvariable: null };
    addBlock(type, duplicatedBlock, order + 1);
  };
  const onInsert = (paramsToAdd) => {
    if (insertMode) {
      const newBlock = { id: "", function: insertBlockDict.name, params: paramsToAdd, outputvariable: null };
      addBlock(type, newBlock, insertMode === "above" ? order : order + 1);
    }
    onCancel();
  };
  const onInsertAbove = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.INSERT_ABOVE_ACTION_CLICKED, {
      app_section: appSection,
      app_action_value: `insert-above-${type}`
    });
    setInsertMode("above");
  };
  const onInsertBelow = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.PIPELINE_RULE_BUILDER.INSERT_BELOW_ACTION_CLICKED, {
      app_section: appSection,
      app_action_value: `insert-below-${type}`
    });
    setInsertMode("below");
  };
  const onUpdate = (params, functionName) => {
    updateBlock(order, type, buildBlockData({ newFunctionName: functionName, newParams: params }));
    onCancel();
  };
  const onSelect = (option) => {
    setCurrentBlockDict(blockDict.find((b) => b.name === option));
  };
  const onInsertSelect = (option) => {
    setInsertBlockDict(blockDict.find((b) => b.name === option));
  };
  const isBlockNegatable = () => type === "condition";
  const options = blockDict.map(({ name, description, rule_builder_name, deprecated }) => ({
    label: rule_builder_name,
    value: name,
    description,
    deprecated
  }));
  const showForm = !block || editMode;
  return /* @__PURE__ */ React.createElement(BlockContainer, null, showForm ? /* @__PURE__ */ React.createElement(
    RuleBlockForm,
    {
      existingBlock: block,
      onAdd,
      onCancel,
      onUpdate,
      onSelect,
      order,
      options,
      outputVariableList,
      selectedBlockDict: currentBlockDict,
      type
    }
  ) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    RuleBlockDisplay,
    {
      block,
      onDelete,
      onEdit,
      onNegate,
      onDuplicate,
      onInsertAbove,
      onInsertBelow,
      selectedBlockDict: currentBlockDict,
      negatable: isBlockNegatable(),
      type
    }
  ), Boolean(insertMode) && /* @__PURE__ */ React.createElement(Modal, { show: true, bsSize: "lg", onHide: resetInsertBlock }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Insert new action ", insertMode, " action N\xB0", order + 1)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(
    RuleBlockForm,
    {
      onAdd: onInsert,
      onCancel,
      onUpdate,
      onSelect: onInsertSelect,
      order: insertMode === "above" ? order : order + 1,
      options,
      outputVariableList,
      selectedBlockDict: insertBlockDict,
      type
    }
  )))));
};
export default RuleBuilderBlock;
